import { useEffect, useRef, useState } from 'react';
import { pointService } from '../../services/api';
import { btnSecundario, pesos } from '../../components/ui/estilos';
import type { Cobro } from './useCobro';

const finales = ['failed', 'canceled', 'expired'];
const etiquetas: Record<string, string> = {
  sending: 'Confirmando envío a la terminal…', created: 'Esperando a la terminal…',
  at_terminal: 'Completa el pago en Point Smart 2.', pending: 'Consultando resultado…',
  action_required: 'Revisa el resultado en la terminal. El cobro requiere conciliación; no vuelvas a cobrar.',
  failed: 'El cobro fue rechazado.', canceled: 'Cobro cancelado en la terminal.', expired: 'El cobro expiró.',
  refunded: 'El pago fue reembolsado. Requiere conciliación.',
};

export default function PointCobro({ c }: { c: Cobro }) {
  const [enabled, setEnabled] = useState(false);
  const [ready, setReady] = useState(false);
  const [intento, setIntento] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const current = useRef(c);
  current.current = c;
  const alive = useRef(true);
  const enCurso = useRef(false);
  const comanda = c.cuenta.comanda_id;
  const subcuenta = c.sel?.tipo === 'mesa' ? c.sel.subcuenta : null;
  const setBloqueo = c.setPointPendiente;

  useEffect(() => {
    alive.current = true;
    setBloqueo(true);
    (async () => {
      try {
        const cfg = (await pointService.config()).data;
        if (!alive.current) return;
        setEnabled(cfg.enabled);
        if (cfg.enabled) {
          const r = await pointService.pendiente(comanda, subcuenta);
          if (!alive.current) return;
          setIntento(r.data);
          setBloqueo(!!r.data);
        } else setBloqueo(false);
      } catch {
        if (alive.current) { setError('No se pudo verificar Point.'); setBloqueo(false); }
      } finally { if (alive.current) setReady(true); }
    })();
    return () => { alive.current = false; setBloqueo(false); };
  }, [comanda, subcuenta, setBloqueo]);

  const consultar = async (id: string) => {
    if (enCurso.current) return;
    enCurso.current = true;
    setBusy(true);
    try {
      const { data } = await pointService.consultar(id);
      if (!alive.current) return;
      // El primer registro devuelve el ticket; reintentos devuelven el pago guardado.
      if (data.pago || (data.comanda_id && data.id && !data.status)) {
        setBloqueo(false);
        current.current.setPagoExitoso(data.pago || data);
        current.current.cerrar();
        return;
      }
      setIntento(data); setError('');
      setBloqueo(!finales.includes(data.status));
    } catch (e: any) {
      if (alive.current) setError(e.response?.data?.error || 'Sin conexión. Consulta el mismo intento antes de cobrar otra vez.');
    } finally { enCurso.current = false; if (alive.current) setBusy(false); }
  };

  useEffect(() => {
    if (!intento?.id || finales.includes(intento.status) || intento.status === 'action_required' || intento.status === 'refunded') return;
    const timer = window.setInterval(() => consultar(intento.id), 4000);
    return () => window.clearInterval(timer);
    // Consultar usa refs para tomar la cuenta y los callbacks actuales.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intento?.id, intento?.status]);

  const crear = async () => {
    if (enCurso.current || (intento && !finales.includes(intento.status))) return;
    enCurso.current = true; setBusy(true); setBloqueo(true); setError('');
    try {
      const { data } = await pointService.crear({ comanda_id: comanda, subcuenta_id: subcuenta, propina: c.totalPropina });
      if (alive.current) { setIntento(data); setBloqueo(!finales.includes(data.status)); setError(data.warning || ''); }
    } catch (e: any) {
      // El backend conserva el intento aun si la respuesta se perdió. Recuperarlo
      // por cuenta evita depender del almacenamiento de este navegador.
      try {
        const r = await pointService.pendiente(comanda, subcuenta);
        if (alive.current) { setIntento(r.data); setBloqueo(!!r.data); }
      } catch { /* estado desconocido: mantener bloqueado */ }
      if (alive.current) setError(e.response?.data?.error || 'No se confirmó el envío. Vuelve a abrir la cuenta para recuperar el intento.');
    } finally { enCurso.current = false; if (alive.current) setBusy(false); }
  };

  if (!ready) return <div role="status">Verificando terminal Point…</div>;
  if (!enabled) return <div style={{ color: 'var(--muted)', fontSize: 13 }}>Activa Point Smart 2 en Configuración › Mercado Pago Point.</div>;
  const pendiente = intento && !finales.includes(intento.status);
  const incompatible = c.montoDescuento > 0 || Object.entries(c.montos).some(([metodo, v]) => metodo !== 'tarjeta' && Number(v) > 0)
    || Object.entries(c.propinas).some(([metodo, v]) => metodo !== 'tarjeta' && Number(v) > 0);
  return <div style={{ padding: 12, border: '1px solid var(--line)', borderRadius: 12, display: 'grid', gap: 8 }}>
    <strong>Point Smart 2</strong>
    {intento && <div role="status">{etiquetas[intento.status] || `Estado: ${intento.status}`} · {pesos(Number(intento.amount))}</div>}
    {error && <div role="alert" style={{ color: 'var(--danger)' }}>{error}</div>}
    {pendiente ? <button style={btnSecundario} disabled={busy} onClick={() => consultar(intento.id)}>{busy ? 'Consultando…' : 'Consultar resultado'}</button>
      : <button style={btnSecundario} disabled={busy || c.pagando || incompatible || c.totalACobrar <= 0} onClick={crear}>
        {busy ? 'Enviando…' : `Cobrar ${pesos(c.totalACobrar + c.totalPropina)} con Point`}
      </button>}
    {!pendiente && <small>Cuenta completa con tarjeta. Captura la propina en Tarjeta. {incompatible ? 'Quita descuentos e importes de otros métodos para usar Point.' : ''}</small>}
  </div>;
}
